import { useCallback, useMemo } from "react"
import { ZERO_ADDRESS } from "@mezo/sdk"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { AnalyticsId, SwapFormValue } from "#/shared/types"
import {
  floatToBigInt,
  getMezoTokenAddress,
  isStablePool,
} from "#/shared/utils"
import { useStepperModalContext } from "#/ui/Modal/StepperModal/StepperModalContext"

import {
  useSegmentButtonClickedEvent,
  useSegmentSwapEvent,
} from "../segment-events"

import useSwapApprove from "./useSwapApprove"
import useSwapTokens from "./useSwapTokens"

const useSwapTransaction = () => {
  const { values, setValues } = useStepperModalContext<SwapFormValue>()
  const { tokenIn, tokenOut, slippage } = values

  const tokenInAddress = getMezoTokenAddress(tokenIn.token)
  const tokenOutAddress = getMezoTokenAddress(tokenOut.token)

  const tokenInDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenIn.token),
  )

  const tokenOutDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenOut.token),
  )

  const amountIn = useMemo(
    () => floatToBigInt(tokenIn.amount, tokenInDecimals),
    [tokenIn.amount, tokenInDecimals],
  )

  const amountOut = useMemo(
    () => floatToBigInt(tokenOut.amount, tokenOutDecimals),
    [tokenOut.amount, tokenOutDecimals],
  )

  const {
    approveSwap,
    refetchAllowance,
    swapAllowance,
    isApprovalError,
    isApprovalPending,
    isWaitingForApprovalReceipt,
  } = useSwapApprove(tokenInAddress ?? ZERO_ADDRESS)

  const {
    swapTokens,
    isSwapTransactionPending,
    isSwapTransactionConfirmed,
    isWaitingForSwapConfirmation,
    isSwapError,
  } = useSwapTokens()

  const { emitButtonClickedSegmentEvent } = useSegmentButtonClickedEvent()
  const { emitSwapCompletedEvent } = useSegmentSwapEvent()

  const needsApproval = useMemo(
    () => (swapAllowance ? amountIn > swapAllowance : true),
    [swapAllowance, amountIn],
  )

  const swapHandler = useCallback(async () => {
    await refetchAllowance()

    if (needsApproval) {
      await approveSwap(amountIn)
      return
    }

    emitButtonClickedSegmentEvent(AnalyticsId.SWAP_COMPLETE)

    if (!tokenIn || !tokenOut || !tokenInAddress || !tokenOutAddress) return

    const transactionHash = await swapTokens({
      amountIn,
      amountOut,
      tokenIn: tokenInAddress,
      tokenOut: tokenOutAddress,
      stable: isStablePool(tokenIn.token, tokenOut.token),
      slippage: Number(slippage),
    })

    if (transactionHash) {
      setValues({ transactionHash })

      emitSwapCompletedEvent({
        amount: Number(tokenIn.amount),
        newAmount: Number(tokenOut.amount),
        swappedFrom: tokenIn.token,
        swappedTo: tokenOut.token,
      })
    }
  }, [
    approveSwap,
    needsApproval,
    emitSwapCompletedEvent,
    emitButtonClickedSegmentEvent,
    swapTokens,
    slippage,
    tokenIn,
    tokenOut,
    tokenInAddress,
    tokenOutAddress,
    setValues,
    amountIn,
    amountOut,
    refetchAllowance,
  ])

  return {
    swap: swapHandler,
    needsApproval,
    isSwapTransactionConfirmed,
    isError: isApprovalError || isSwapError,
    isSwapTransactionPending,
    isWaitingForSwapConfirmation,
    isWaitingForApproval: isApprovalPending || isWaitingForApprovalReceipt,
  }
}

export default useSwapTransaction
